import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Alert } from "@/components/ui/alert";
import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { PageSkeleton } from "@/components/PageSkeleton";
import { Shell } from "@/components/Shell";
import { SignIn } from "@/features/session/SignIn";
import { useMe } from "@/features/session/useMe";

/**
 * The three doors every beheerder-only page has, in one place so a new page cannot forget one:
 * not signed in, signed in without the claim, and the page itself.
 */
export function BeheerderShell({
  titel,
  uitleg,
  children,
}: {
  titel: string;
  uitleg: ReactNode;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const { data: me, isPending } = useMe();

  if (isPending) {
    return (
      <Shell>
        <PageSkeleton />
      </Shell>
    );
  }

  if (!me) {
    return (
      <Shell>
        <SignIn />
      </Shell>
    );
  }

  // Letterhead, not the application: every page the sidebar links to would refuse them too,
  // so all this session can offer is where to go instead and the way out.
  //
  // The fuller wording travelled here with the front door. It used to sit on the index page,
  // which was Ploegen; `/` is the Overzicht now, and this is the shell it wears - so a
  // korpscontact who lands on the backoffice root still reads where to go rather than a bare
  // refusal.
  if (!me.isBeheerder) {
    return (
      <Shell name={me.name} letterhead>
        <GeenToegangKaart />
      </Shell>
    );
  }

  return (
    <Shell name={me.name} wide>
      {me.isReadOnly && <Alert className="mb-4">{t("readOnlyBanner")}</Alert>}
      <header className="mb-6 space-y-1">
        <h1 className="text-2xl font-bold tracking-[-0.02em]">{titel}</h1>
        <p className="text-sm text-muted-foreground">{uitleg}</p>
      </header>
      {children}
    </Shell>
  );
}

function GeenToegangKaart() {
  const { t } = useTranslation();

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("doors.noAccessBackoffice")}</CardTitle>
        <CardDescription>
          {t("doors.noAccessBackofficeBefore")}
          <a className="jb-link" href={`https://${t("aanmeldsite")}`}>
            {t("aanmeldsite")}
          </a>
          {t("doors.noAccessBackofficeAfter")}
        </CardDescription>
      </CardHeader>
    </Card>
  );
}
